import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { casesApi } from '../../services/cases';
import { clientsApi } from '../../services/clients';
import { Case, Client } from '../../types';
import { CaseFilter, StatusFilter } from './CaseFilter';
import { CaseSearch } from './CaseSearch';
import { CaseList } from './CaseList';
import { filterCasesByStatus, filterCasesBySearch } from './caseViewUtils';

/**
 * CaseView page component - displays all cases for an advocate organized by student name.
 * 
 * Features:
 * - Fetches cases and clients on mount
 * - Manages filter and search state
 * - Defaults filter to 'active'
 * - Composes CaseFilter, CaseSearch, CaseList components
 * - Handles loading, error, and empty states
 * - Includes "New Case" button
 * 
 * Requirements: 1.1, 3.3, 5.3, 6.1, 6.2, 7.1, 7.2
 */
export const CaseView: React.FC = () => {
  const navigate = useNavigate();
  
  // Data state
  const [cases, setCases] = useState<Case[]>([]);
  const [clients, setClients] = useState<Map<string, Client>>(new Map());
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state - default to 'active' per requirement 3.3
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Load cases and associated client data
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all cases (backend handles advocate/tenant-admin filtering)
      const casesResponse = await casesApi.list();
      setCases(casesResponse.cases);
      
      // Fetch clients for parent/guardian names
      // Client fetch failures should not block the view
      try {
        const clientsResponse = await clientsApi.list();
        const clientMap = new Map<string, Client>();
        clientsResponse.clients.forEach(client => {
          clientMap.set(client.clientId, client);
        });
        setClients(clientMap);
      } catch (clientError) {
        // Log but don't block - cases will show without parent names
        console.error('Failed to load clients:', clientError);
      }
    } catch (err) {
      setError('Failed to load cases. Please try again.');
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Handle case card click - navigate to case detail
   */
  const handleCaseClick = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  /**
   * Handle retry button click
   */
  const handleRetry = () => {
    loadData();
  };

  // Apply filters to cases (client-side filtering per requirement 3.4)
  const filteredCases = filterCasesBySearch(
    filterCasesByStatus(cases, statusFilter),
    searchQuery
  );

  /**
   * Generate appropriate empty state message based on current filters
   */
  const getEmptyStateMessage = (): { title: string; subtitle: string } => {
    const hasSearch = searchQuery.trim().length > 0;
    const hasStatusFilter = statusFilter !== 'all';
    
    // No cases at all
    if (cases.length === 0) {
      return {
        title: "You don't have any cases yet",
        subtitle: "Create your first case to get started.",
      };
    }
    
    // Filter + search combination
    if (hasStatusFilter && hasSearch) {
      const statusLabel = statusFilter === 'on-hold' ? 'on-hold' : statusFilter;
      return {
        title: `No ${statusLabel} cases found matching '${searchQuery}'`,
        subtitle: "Try adjusting your search or filter.",
      };
    }
    
    // Search only
    if (hasSearch) {
      return {
        title: `No cases found matching '${searchQuery}'`,
        subtitle: "Try a different search term.",
      };
    }
    
    // Status filter only
    if (hasStatusFilter) {
      const statusLabel = statusFilter === 'on-hold' ? 'on-hold' : statusFilter;
      return {
        title: `No ${statusLabel} cases found`,
        subtitle: "Try selecting a different filter.",
      };
    }
    
    // Fallback (shouldn't reach here if cases.length > 0)
    return {
      title: "No cases found",
      subtitle: "",
    };
  };

  return (
    <Layout>
      <div style={{ padding: '24px' }} data-testid="case-view">
        {/* Header with title and New Case button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <h1 style={{ margin: 0 }}>Cases</h1>
          <button
            data-testid="new-case-button"
            onClick={() => navigate('/cases/new')}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#1565c0';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#1976d2';
            }}
          >
            + New Case
          </button>
        </div>

        {/* Filter and Search controls */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '24px' 
        }}>
          <CaseFilter 
            value={statusFilter} 
            onChange={setStatusFilter} 
          />
          <CaseSearch 
            value={searchQuery} 
            onChange={setSearchQuery} 
          />
        </div>

        {/* Error state with retry */}
        {error && (
          <div 
            data-testid="case-view-error"
            style={{ 
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{error}</span>
            <button
              data-testid="retry-button"
              onClick={handleRetry}
              style={{
                backgroundColor: '#c62828',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div 
            data-testid="case-view-loading"
            style={{ 
              textAlign: 'center', 
              padding: '48px',
              color: '#666',
            }}
          >
            <div style={{ 
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid #e0e0e0',
              borderTopColor: '#1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <style>
              {`@keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
            <p style={{ marginTop: '16px' }}>Loading cases...</p>
          </div>
        )}

        {/* Content - only show when not loading and no error */}
        {!loading && !error && (
          <>
            {filteredCases.length === 0 ? (
              // Empty state
              <div 
                data-testid="case-view-empty"
                style={{ 
                  textAlign: 'center', 
                  padding: '48px 24px',
                  color: '#666',
                }}
              >
                <p style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
                  {getEmptyStateMessage().title}
                </p>
                <p style={{ fontSize: '14px', margin: 0 }}>
                  {getEmptyStateMessage().subtitle}
                </p>
                {cases.length === 0 && (
                  <button
                    data-testid="empty-state-new-case-button"
                    onClick={() => navigate('/cases/new')}
                    style={{
                      marginTop: '24px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Create Your First Case
                  </button>
                )}
              </div>
            ) : (
              // Case list
              <CaseList 
                cases={filteredCases}
                clients={clients}
                onCaseClick={handleCaseClick}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};
